import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Divider, Button, Chip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import LockOpenIcon from '../../../../assets/LockOpen.svg'
import LockClosedIcon from '../../../../assets/LockClosed.svg'
import TrashIcon from '../../../../assets/TrashIconRed.svg'

// components
import InfoIconWithText from '../../../../components/infoIconWithText'
import ResetPassword from '../../../../components/resetPassword'
import ForgotPassword from '../../../../components/forgotPassword'
import SendActivationEmail from '../../../../components/sendActivationEmail'
import SendReActivationEmail from '../../../../components/sendReActivationEmail'
import ResetMultiFactor from '../../../../components/resetMultiFactor'
import DeleteUserModal from '../DeleteUserModal/DeleteUserModal'

// hooks
import useDialogVisibility from '../../../../hooks/useDialogVisibility'

// helpers
import {
  getEmail,
  getPhone,
  getCountryLabel,
  getStatus,
  getLocale,
  getCountry,
  getGuid,
  shouldEnableForgotPassword,
  shouldEnableSendActivationEmail,
  shouldEnableResetPassword,
  shouldEnableSendReActivationEmail,
  shouldEnableResetMultiFactor,
} from '../../../../helpers/users'
import {
  getGroupsWithColour,
  getRoleDisplayName
} from '../../../../helpers/user'
import { ColorCodeGroups } from '../../../../utils/general'
import { groupRoleMapping } from '../../../userProfile/components/userGroupsField/groupRoleMapper'

// constants
import { EMPTY_PLACE_HOLDER } from '../../../../constants/general'

// styles
import styles from './userInfo.module.scss'
import { fetchCountries } from '../../../../services/data'

// store
import {
  getUserObject,
  getUserCallStatus,
} from '../../../../store/selectors/userProfile'
import UnlockUserModal from '../UnlockUserModal/UnlockUserModal'
import Alert from '../../../../components/Alert/Alert'

const editStyle = {
  backgroundColor: '#eb1700',
  '&:hover': {
    backgroundColor: '#CC1400',
  },
  '&:disabled': {
    backgroundColor: '#665A47',
  },
  '&:focus': {
    backgroundColor: '#CC1400',
  },
}

const cancelStyle = {
  backgroundColor: 'rgba(98, 81, 55, 0.1)',
  '&:hover': {
    backgroundColor: 'rgba(102,90,71, 0.20)',
  },
  '&:focus': {
    backgroundColor: 'rgba(112, 99, 79, 0.1)',
  },
}

const handleDisplayGroupName = (name) => {
  const splitedName = name.split('-')

  return splitedName[0].trim()
}

const renderGroups = (groups = []) => {
  const { t } = useTranslation()
  if (groups.length === 0) {
    return <div className={styles.groupsValue}>{t('view-user-no-groups')}</div>
  }

  const getUserApplicationsRoles = () => {
    const appRoles = groups.map((group) => group.display)

    return { groups: appRoles }
  }

  let colorsArray
  const allGroups = getUserApplicationsRoles()

  if (allGroups) {
    const result = groupRoleMapping(allGroups)
    colorsArray = result
  }

  const appGroups = colorsArray !== undefined ? Object.keys(colorsArray) : []
  const colors = ColorCodeGroups(appGroups)

  return (
    <div className={styles.groupsContainer}>
      {groups.map((group) => (
        <Chip
          className={styles.group}
          label={group?.display || group?.value}
          sx={{
            border: '1px solid #ECE9E6',
            borderColor: (
              colors[handleDisplayGroupName(group.display)] === '#ffffff'
                ? ' #959392B2'
                : colors[handleDisplayGroupName(group.display)]),
            backgroundColor: colors[handleDisplayGroupName(group.display)],
          }}
        />
      ))}
    </div>
  )
}

const UserInfo = ({ user }) => {
  const { t } = useTranslation()
  const [countriesList, setCountriesList] = useState([])
  const [userCountry, setUserCountry] = useState('')
  const [userStatus, setUserStatus] = useState('')
  const [showAlert, setShowAlert] = useState(false)
  const [showAlertDeleteUser, setShowAlertDeleteUser] = useState(false)
  const managerData = useSelector(getUserObject)
  const dataLoaded = useSelector(getUserCallStatus)
  const [managerCanDeleteUser, setManagerCanDeleteUser] = useState(false)
  const [managerCanUnlockUser, setManagerCanUnlockUser] = useState(false)
  const deleteUserGroupValue = 'xIAM API Gateway - Delete User'
  const superAdminGroupValue = 'xIAM API Gateway - Super Admin'
  const { groups } = user || {}
  const navigate = useNavigate()
  const accessRole = getRoleDisplayName()
  const groupsWithColour = getGroupsWithColour(groups)
  const [
    isResetPasswordDialogOpen,
    openResetPasswordDialog,
    closeResetPasswordDialog,
  ] = useDialogVisibility(false)
  const [
    isForgotPasswordDialogOpen,
    openForgotPasswordDialog,
    closeForgotPasswordDialog,
  ] = useDialogVisibility(false)
  const [
    isSendActivationEmailDialogOpen,
    openSendActivationEmailDialog,
    closeSendActivationDialog,
  ] = useDialogVisibility(false)
  const [
    isSendReActivationEmailDialogOpen,
    openSendReActivationEmailDialog,
    closeSendReActivationDialog,
  ] = useDialogVisibility(false)
  const [
    isResetMultiFactorDialogOpen,
    openResetMultiFactorDialog,
    closeResetMultiFactorDialog,
  ] = useDialogVisibility(false)
  const [isDeleteUserDialogOpen, openDeleteUserDialog, closeDeleteUserDialog] =
    useDialogVisibility(false)
  const [isUnlockUserDialogOpen, openUnlockUserDialog, closeUnlockUserDialog] =
    useDialogVisibility(false)
  const handleResetPasswordClick = (e) => {
    e.stopPropagation()
    openResetPasswordDialog()
  }

  const handleForgotPasswordClick = (e) => {
    e.stopPropagation()
    openForgotPasswordDialog()
  }

  const handleResendActivationEmailClick = (e) => {
    e.stopPropagation()
    openSendActivationEmailDialog()
  }

  const handleSendReActivationEmailClick = (e) => {
    e.stopPropagation()
    openSendReActivationEmailDialog()
  }

  const handleResetMultiFactorClick = (e) => {
    e.stopPropagation()
    openResetMultiFactorDialog()
  }

  const handleDeleteUserClick = (e) => {
    e.stopPropagation()
    openDeleteUserDialog()
  }
  const handleUnlockUserClick = (e) => {
    e.stopPropagation()
    openUnlockUserDialog()
  }
  const handleUnlockSuccess = () => {
    if (getStatus(user) === 'ACTIVE' && managerCanUnlockUser) {
      setShowAlert(true)
      setUserStatus('ACTIVE')
    }
  }
  const handleDeleteFailure = () => {
    setShowAlertDeleteUser(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
  const handleNewDeleteUserRequest = () => {
    setShowAlertDeleteUser(false)
  }
  const handleAlertClose = () => {
    setShowAlert(false)
  }
  const handleAlertCloseDeleteUser = () => {
    setShowAlertDeleteUser(false)
  }
  const getCountriesList = async () => {
    try {
      const response = await fetchCountries()
      if (response && response.status === 200) {
        setCountriesList(response.data[0])
      }
    } catch (error) {
      setCountriesList([])
    }
  }

  const handleManagerData = (managerGroupsArray) => {
    let hasFound
    if (managerGroupsArray) {
      hasFound = managerGroupsArray.find(
        ({ display }) =>
          display === deleteUserGroupValue || display === superAdminGroupValue
      )
    }
    for (let uindex = 0; uindex < user.groups.length; uindex++) {
      const ugroup = user.groups[uindex].display.split(' - ')
      for (let index = 0; index < managerGroupsArray.length; index++) {
        const group = managerGroupsArray[index].display.split(' - ')
        if (group[0] === ugroup[0]) {
          if (group[1] === 'Admin' && (ugroup[1] === 'Users' || ugroup[0] === group[0])) {
            setManagerCanUnlockUser(true)
            break
          }
        }
      }
      if (managerCanUnlockUser) {
        break
      }
    }

    if (accessRole === 'Super Admin') {
      setManagerCanUnlockUser(true)
    }

    if (hasFound) {
      return setManagerCanDeleteUser(true)
    }

    return setManagerCanDeleteUser(false)
  }

  useEffect(() => {
    getCountriesList()
    setUserStatus(getStatus(user))
  }, [])

  useEffect(() => {
    if (dataLoaded === 'loading') return
    handleManagerData(managerData?.groups)
  }, [dataLoaded])

  useEffect(() => {
    if (countriesList.length > 0 && getCountry(user)) {
      setUserCountry(getCountryLabel(countriesList, user))
    } else if (getCountry(user)) {
      setUserCountry(getCountry(user))
    } else {
      setUserCountry('-')
    }
  }, [countriesList])

  return (
    <>
      <div className={styles.userInfoContainer}>
        {showAlert && (
          <Alert
            alertStyle="success-one-line"
            message="User was successfully unlocked"
            onClose={handleAlertClose}
            customStyles={{ marginTop: '21px' }}
          />
        )}
        {showAlertDeleteUser && (
          <Alert
            alertStyle="error-one-line"
            message="Unable to delete user"
            onClose={handleAlertCloseDeleteUser}
            customStyles={{ marginTop: '21px' }}
          />
        )}
        {userStatus === 'LOCKED_OUT' ? (
          <div
            className={styles.lockedStatusContainer}
            data-testid="userLockedStatusContainer"
          >
            <img src={LockClosedIcon} alt="" style={{ height: '24px' }} />
            <div className={styles.label}>{t('view-user-account-locked')}</div>
            <button
              type="button"
              className={styles.userActions}
              onClick={handleUnlockUserClick}
              data-testid="view-user-unlock-user"
            >
              {t('view-user-unlock-user')}
            </button>
          </div>
        ) : (
          <div
            className={styles.unlockedStatusContainer}
            data-testid="userUnlockedStatusContainer"
          >
            <img src={LockOpenIcon} alt="" style={{ height: '24px' }} />
            <div className={styles.label}>
              {t('view-user-account-unlocked')}
            </div>
          </div>
        )}
        <div className={styles.textContainer}>
          <div className={styles.label}>
            <InfoIconWithText
              label={t('view-user-okta-id')}
              labelClassName={styles.guidLabel}
              helpText={t('view-user-okta-id-help')}
            />
          </div>
          <div className={styles.value}>
            {getGuid(user) || EMPTY_PLACE_HOLDER}
          </div>
        </div>
        <div className={styles.textContainer}>
          <div className={styles.label}>{t('view-user-email')}</div>
          <div>
            <div className={styles.value}>{getEmail(user)}</div>
            <div className={styles.userActionsContainer}>
              {shouldEnableResetPassword(user) && (
                <button
                  type="button"
                  className={styles.userActions}
                  onClick={handleResetPasswordClick}
                  data-testid="userInfoResetPasswordButton"
                >
                  {t('view-user-reset-password')}
                </button>
              )}
              {shouldEnableForgotPassword(user) && (
                <button
                  className={styles.userActions}
                  type="button"
                  onClick={handleForgotPasswordClick}
                  data-testid="userInfoForgotPasswordButton"
                >
                  {t('view-user-forgot-password')}
                </button>
              )}
              {shouldEnableSendActivationEmail(user) && (
                <button
                  className={styles.userActions}
                  type="button"
                  onClick={handleResendActivationEmailClick}
                  data-testid="userInfoResendActivationEmailButton"
                >
                  {t('view-user-activation-email')}
                </button>
              )}
              {shouldEnableSendReActivationEmail(user) && (
                <button
                  className={styles.userActions}
                  type="button"
                  onClick={handleSendReActivationEmailClick}
                  data-testid="userInfoSendReActivationEmailButton"
                >
                  {t('view-user-re-activation-email')}
                </button>
              )}
              {shouldEnableResetMultiFactor(user) && (
                <button
                  className={styles.userActions}
                  type="button"
                  onClick={handleResetMultiFactorClick}
                  data-testid="userInfoResetMultiFactorButton"
                >
                  {t('view-user-reset-mfa')}
                </button>
              )}
            </div>
          </div>
        </div>
        <div className={styles.mobileTextContainer}>
          <div className={styles.label}>{t('view-user-mobile')}</div>
          <div className={styles.value}>
            {getPhone(user) || EMPTY_PLACE_HOLDER}
          </div>
        </div>
        <div className={styles.textContainer}>
          <div className={styles.label}>{t('view-user-country')}</div>
          <div className={styles.value}>
            {userCountry || EMPTY_PLACE_HOLDER}
          </div>
        </div>
        <div className={styles.textContainer}>
          <div className={styles.label}>{t('view-user-locale')}</div>
          <div className={styles.value}>
            {getLocale(user) || EMPTY_PLACE_HOLDER}
          </div>
        </div>
        <div className={styles.textContainer}>
          <div className={styles.label}>{t('view-user-status')}</div>
          <div className={styles.value}>{userStatus}</div>
        </div>
        <div className={styles.textContainer}>
          <div className={styles.label}>{t('view-user-language')}</div>
          <div className={styles.value}>
            {user.preferredLanguage || EMPTY_PLACE_HOLDER}
          </div>
        </div>
        <div className={styles.textContainerGroup}>
          <div className={styles.label}>{t('view-user-groups')}</div>
          {renderGroups(groupsWithColour)}
        </div>
        <Divider />
        <div className={styles.actionButtonsContainer}>
          <div style={{ display: 'flex', alignItems: 'stretch' }}>
            <Button
              onClick={() => navigate(`/edit-user/${user.id}`)}
              className="primary-btn"
              variant="contained"
              disableElevation
              sx={{
                ...editStyle,
                marginTop: '20px',
                minHeight: '48px',
                width: '127px',
              }}
              data-testid="userInfoEditUserButton"
            >
              {t('view-user-edit')}
            </Button>
            <Button
              onClick={() => navigate('/search-user')}
              className="secondary-btn"
              variant="contained"
              disableElevation
              sx={{
                ...cancelStyle,
                marginTop: '20px',
                color: '#000',
                minHeight: '48px',
                width: '217px',
              }}
              data-testid="userInfoGoBackButton"
            >
              {t('view-user-back-to-edit')}
            </Button>
          </div>
          {managerCanDeleteUser && (
            <Button
              className={`secondary-btn ${styles.deleteButton}`}
              onClick={handleDeleteUserClick}
              type="button"
              startIcon={(
                <img
                  src={TrashIcon}
                  alt=""
                  style={{ height: '20px', marginLeft: '4px' }}
                />
              )}
              variant="contained"
              disableElevation
              sx={{
                ...cancelStyle,
                marginTop: '20px',
                color: 'red',
                minHeight: '48px',
                width: '164px',
              }}
              data-testid="userDeleteButton"
            >
              {t('view-user-delete-user')}
            </Button>
          )}
        </div>
      </div>
      <ResetPassword
        showModal={isResetPasswordDialogOpen}
        handleClose={closeResetPasswordDialog}
        userId={user.id}
      />
      <ForgotPassword
        showModal={isForgotPasswordDialogOpen}
        handleClose={closeForgotPasswordDialog}
        userId={user.id}
        userEmail={getEmail(user)}
      />
      <SendActivationEmail
        showModal={isSendActivationEmailDialogOpen}
        handleClose={closeSendActivationDialog}
        userId={user.id}
        userEmail={getEmail(user)}
      />
      <SendReActivationEmail
        showModal={isSendReActivationEmailDialogOpen}
        handleClose={closeSendReActivationDialog}
        userId={user.id}
        userEmail={getEmail(user)}
      />
      {isResetMultiFactorDialogOpen && (
        <ResetMultiFactor
          showModal={isResetMultiFactorDialogOpen}
          handleClose={closeResetMultiFactorDialog}
          userId={user.id}
        />
      )}
      <DeleteUserModal
        showModal={isDeleteUserDialogOpen}
        handleClose={closeDeleteUserDialog}
        user={user}
        onDeleteFailure={handleDeleteFailure}
        onNewRequest={handleNewDeleteUserRequest}
      />
      <UnlockUserModal
        showModal={isUnlockUserDialogOpen}
        handleClose={closeUnlockUserDialog}
        user={user}
        onUnlockSuccess={handleUnlockSuccess}
      />
    </>
  )
}

UserInfo.propTypes = {
  user: PropTypes.object,
}

UserInfo.defaultProps = {
  user: {},
}

export default UserInfo
